import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ClipboardPaste, Loader2, X, Upload, CheckCircle2, AlertTriangle, ExternalLink, Layers } from 'lucide-react';

const BATCH = 8;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
const PHASE = { upload: 'Upload', reading: 'KI liest Screenshots', saving: 'Firmen werden gespeichert', done: 'fertig', failed: 'Fehler' };

function Results({ results }) {
  if (!results?.length) return null;
  return (
    <div className="max-h-80 overflow-auto border border-border rounded-sm" data-testid="ii-bulk-results">
      <table className="w-full text-[11px]">
        <thead className="bg-secondary/40 text-muted-foreground text-left">
          <tr><th className="px-2 py-1 font-medium">Firma</th><th className="px-2 py-1 font-medium">Status</th><th className="px-2 py-1 font-medium text-right">Shots</th><th className="px-2 py-1 font-medium text-right">Deals</th><th className="px-2 py-1 font-medium text-right">Funds</th><th className="px-2 py-1 font-medium text-right">Kontakte</th><th className="px-2 py-1" /></tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {results.map((r, i) => (
            <tr key={i} data-testid={`ii-bulk-result-${i}`} className={r.error ? 'text-red-400' : ''}>
              <td className="px-2 py-1 truncate max-w-[260px]">{r.name}{r.error && <span className="block text-[10px]">{r.error}</span>}</td>
              <td className="px-2 py-1">
                {r.error ? 'Fehler' : <span className={r.created ? 'text-amber-300' : 'text-sky-300'}>{r.created ? 'neu angelegt' : 'ergänzt'}</span>}
                {r.uncertain > 0 && <span className="ml-1 text-[10px] text-amber-300/80 inline-flex items-center gap-0.5" title="Screenshots ohne sichtbaren Firmennamen wurden dieser Firma zugeordnet — bitte prüfen"><AlertTriangle className="w-3 h-3" />{r.uncertain} unsicher</span>}
              </td>
              <td className="px-2 py-1 text-right font-mono">{r.screenshots}</td>
              <td className="px-2 py-1 text-right font-mono">{r.deals_saved ?? '—'}{r.deals_skipped ? <span className="text-muted-foreground/60"> (+{r.deals_skipped} Dubl.)</span> : ''}</td>
              <td className="px-2 py-1 text-right font-mono">{r.funds ?? '—'}</td>
              <td className="px-2 py-1 text-right font-mono">{r.people ?? '—'}</td>
              <td className="px-2 py-1 text-right">{r.fund_id && <a href={`/infra-funds/${r.fund_id}`} className="text-sky-300 hover:text-sky-200 inline-flex items-center gap-0.5" data-testid={`ii-bulk-open-${i}`}><ExternalLink className="w-3 h-3" /> öffnen</a>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobView({ job, onReset }) {
  const running = job.status === 'running';
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="space-y-2" data-testid="ii-bulk-job">
      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        {running ? <Loader2 className="w-4 h-4 animate-spin text-amber-300" /> : job.status === 'done' ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
        <span className="text-foreground font-medium" data-testid="ii-bulk-status">
          {running ? `${PHASE[job.phase] || 'läuft'} — ${job.done}/${job.total} Screenshots` : job.status === 'done' ? `Fertig — ${job.total} Screenshots → ${(job.results || []).length} Firmen` : `Abgebrochen: ${job.error}`}
        </span>
        {job.failed > 0 && <span className="text-red-400">{job.failed} nicht lesbar</span>}
        {job.stale && <span className="text-amber-300">keine Aktualisierung seit 10 min — nach Neustart wird fortgesetzt</span>}
        <span className="text-muted-foreground/60 ml-auto">{fmtTime(job.created_at)}</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} /></div>
      {running && job.firms_seen?.length > 0 && (
        <p className="text-[11px] text-muted-foreground" data-testid="ii-bulk-firms-seen"><Layers className="w-3 h-3 inline mr-1" />Erkannt: {job.firms_seen.join(' · ')}</p>
      )}
      <Results results={job.results} />
      {!running && <button onClick={onReset} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="ii-bulk-new">nächsten Stapel einfügen</button>}
    </div>
  );
}

export function IIBulkImport({ onSaved }) {
  const [shots, setShots] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const timer = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const jobs = (await api.iiJobs()).data.jobs || [];
      setHistory(jobs);
      const running = jobs.find(j => j.status === 'running');
      if (running) setJob(j => j || running);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    if (!job || job.status !== 'running') { clearInterval(timer.current); return undefined; }
    timer.current = setInterval(async () => {
      try {
        const r = await api.iiJobGet(job.id);
        setJob(r.data);
        if (r.data.status !== 'running') {
          clearInterval(timer.current); loadJobs(); onSaved?.();
          toast.success(`Massen-Import fertig — ${(r.data.results || []).length} Firmen aus ${r.data.total} Screenshots`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(timer.current);
  }, [job?.id, job?.status, onSaved, loadJobs]);

  const addFiles = useCallback((files) => {
    const imgs = [...files].filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;
    setShots(prev => [...prev, ...imgs.map((f, i) => new File([f], f.name && f.name !== 'image.png' ? f.name : `shot-${String(prev.length + i + 1).padStart(4, '0')}.png`, { type: f.type }))]);
  }, []);

  useEffect(() => {
    const h = (e) => {
      const items = [...(e.clipboardData?.items || [])].filter(i => i.type.startsWith('image/'));
      if (!items.length) return;
      e.preventDefault();
      addFiles(items.map(i => i.getAsFile()).filter(Boolean));
    };
    window.addEventListener('paste', h);
    return () => window.removeEventListener('paste', h);
  }, [addFiles]);

  const start = async () => {
    setUploading({ done: 0, total: shots.length });
    try {
      const { data: { id } } = await api.iiJobCreate();
      for (let i = 0; i < shots.length; i += BATCH) {
        await api.iiJobFiles(id, shots.slice(i, i + BATCH), i);
        setUploading({ done: Math.min(i + BATCH, shots.length), total: shots.length });
      }
      await api.iiJobStart(id);
      setJob((await api.iiJobGet(id)).data);
      setShots([]);
    } catch (e) { toast.error(e.response?.data?.detail || (e.response ? `Upload fehlgeschlagen (HTTP ${e.response.status})` : 'Upload fehlgeschlagen — Verbindung unterbrochen oder Timeout')); }
    setUploading(null);
  };

  const past = history.filter(j => j.id !== job?.id).slice(0, 4);
  return (
    <div className="space-y-3" data-testid="ii-bulk">
      {job ? <JobView job={job} onReset={() => setJob(null)} /> : (
        <>
          <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }} onClick={() => !shots.length && inputRef.current?.click()}
            data-testid="ii-bulk-dropzone"
            className={`border border-dashed rounded-sm p-4 transition-colors ${shots.length ? 'border-border' : 'border-amber-500/40 hover:border-amber-400 cursor-pointer'}`}>
            {shots.length ? (
              <div className="flex items-center gap-3 flex-wrap" data-testid="ii-bulk-shots">
                <span className="text-sm text-foreground font-medium" data-testid="ii-bulk-count">{shots.length} Screenshots gesammelt</span>
                <span className="text-[11px] text-muted-foreground">Reihenfolge = Einfüge-Reihenfolge; Seiten ohne sichtbaren Firmennamen werden der zuletzt erkannten Firma zugeordnet.</span>
                <button onClick={() => setShots(prev => prev.slice(0, -1))} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 ml-auto" data-testid="ii-bulk-remove-last"><X className="w-3 h-3" /> letzten entfernen</button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-3 inline-flex items-center gap-2 w-full justify-center">
                <ClipboardPaste className="w-4 h-4 text-amber-300" /> Hunderte Screenshots nacheinander mit Strg+V einfügen oder hierher ziehen — mehrere Firmen gemischt sind ok (Overview-Seite jeder Firma zuerst).
              </p>
            )}
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ''; }} data-testid="ii-bulk-file-input" />
          </div>
          {shots.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={start} disabled={!!uploading} data-testid="ii-bulk-start-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-4 h-9 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? `Hochladen ${uploading.done}/${uploading.total}…` : `Massen-Import starten · ${shots.length} Screenshots`}
              </button>
              <button onClick={() => inputRef.current?.click()} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="ii-bulk-add-more">Dateien hinzufügen</button>
              <button onClick={() => setShots([])} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="ii-bulk-clear">leeren</button>
              <span className="text-[10px] text-muted-foreground/70">Läuft im Hintergrund (4 parallel, ~30 s/Bild) — Seite kann verlassen werden</span>
            </div>
          )}
        </>
      )}
      {past.length > 0 && (
        <div className="space-y-1" data-testid="ii-bulk-history">
          <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Bisherige Massen-Importe</p>
          {past.map(j => (
            <button key={j.id} onClick={() => api.iiJobGet(j.id).then(r => setJob(r.data)).catch(() => {})} className="w-full text-left text-[11px] flex items-center gap-2 flex-wrap hover:bg-secondary/30 rounded-sm px-2 py-1" data-testid={`ii-bulk-history-${j.id}`}>
              <span className="text-muted-foreground/70 font-mono">{fmtTime(j.created_at)}</span>
              <span className="text-foreground">{j.total} Screenshots</span>
              <span className="text-amber-300 font-mono">{(j.results || []).length} Firmen</span>
              <span className={j.status === 'done' ? 'text-emerald-300' : j.status === 'running' ? 'text-amber-300' : 'text-red-400'}>{j.status === 'done' ? 'fertig' : j.status === 'running' ? 'läuft' : 'Fehler'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
